import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck,
  Calendar2Week,
  CalendarPlus,
  ChevronRight,
  XCircle,
} from "react-bootstrap-icons";

import PageHeader from "../components/PageHeader";
import LoadingSpinner from "../components/LoadingSpinner";
import { ApiConnectionString } from "../constants/ApiConntection";
import { useAuthContext } from "../hooks/useAuthContext";

export default function DashboardPage({ profile }) {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.token) {
      setIsLoading(false);
      return;
    }

    fetchAppointments();
  }, [user]);

  const fetchAppointments = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await axios.get(
        `${ApiConnectionString}/api/client/appointments`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.appointments ||
          response.data?.Appointments ||
          [];

      setAppointments(data);
    } catch (error) {
      console.error(error);

      if (error.response?.status === 403) {
        setError(
          "You do not currently have permission to view appointments."
        );
      } else {
        setError("Your appointments could not be loaded.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getAppointmentId = (appointment) => {
    return appointment?.id || appointment?.Id;
  };

  const getAppointmentStart = (appointment) => {
    return (
      appointment?.start ||
      appointment?.Start ||
      appointment?.appointmentDate ||
      appointment?.AppointmentDate ||
      appointment?.dateAndTime ||
      appointment?.DateAndTime ||
      ""
    );
  };

  const getAppointmentFinish = (appointment) => {
    return (
      appointment?.finish ||
      appointment?.Finish ||
      appointment?.finishTime ||
      appointment?.FinishTime ||
      appointment?.end ||
      appointment?.End ||
      ""
    );
  };

  const getTherapistName = (appointment) => {
    return (
      appointment?.therapistName ||
      appointment?.TherapistName ||
      appointment?.therapist?.name ||
      appointment?.Therapist?.Name ||
      ""
    );
  };

  const formatAppointmentDate = (appointment) => {
    const displayDate =
      appointment?.displayDate || appointment?.DisplayDate;

    if (displayDate) {
      return displayDate;
    }

    const start = new Date(getAppointmentStart(appointment));

    if (Number.isNaN(start.getTime())) {
      return "Date unavailable";
    }

    return start.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (value) => {
    if (!value) {
      return "";
    }

    if (
      typeof value === "string" &&
      /^\d{1,2}:\d{2}/.test(value)
    ) {
      return value.substring(0, 5);
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAppointmentTime = (appointment) => {
    const suppliedTime = appointment?.time || appointment?.Time;

    if (suppliedTime) {
      return suppliedTime;
    }

    const start = formatTime(getAppointmentStart(appointment));
    const finish = formatTime(getAppointmentFinish(appointment));

    if (start && finish) {
      return `${start} – ${finish}`;
    }

    return start || "Time unavailable";
  };

  const getAppointmentDuration = (appointment) => {
    const start = new Date(getAppointmentStart(appointment));
    const finish = new Date(getAppointmentFinish(appointment));

    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(finish.getTime())
    ) {
      return "";
    }

    const difference = finish.getTime() - start.getTime();
    const minutes = Math.round(difference / 60000);

    if (minutes <= 0) {
      return "";
    }

    return `${minutes} minute session`;
  };

  const upcomingAppointments = appointments
    .filter((appointment) => {
      const start = new Date(getAppointmentStart(appointment));

      if (Number.isNaN(start.getTime())) {
        return false;
      }

      return start.getTime() >= Date.now();
    })
    .sort((first, second) => {
      return (
        new Date(getAppointmentStart(first)).getTime() -
        new Date(getAppointmentStart(second)).getTime()
      );
    });

  const nextAppointment = upcomingAppointments[0];

  const viewNextAppointment = () => {
    const appointmentId = getAppointmentId(nextAppointment);

    if (appointmentId) {
      navigate(`/appointments/${appointmentId}`);
      return;
    }

    navigate("/appointments");
  };

  if (isLoading) {
    return (
      <div className="page-shell">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="page-shell">
      <PageHeader
        title={`Hello, ${profile?.firstName || "there"}`}
        text="View your next appointment or manage your bookings."
      />

      <div className="dashboard-grid">
        {error ? (
          <section className="dashboard-card next-card">
            <div className="card-icon dashboard-error-icon">
              <XCircle />
            </div>

            <div>
              <span className="card-label">
                Unable to load appointments
              </span>

              <h2>Something went wrong</h2>

              <p>{error}</p>
            </div>

            <button
              type="button"
              className="secondary-btn"
              onClick={fetchAppointments}
            >
              Try again
            </button>
          </section>
        ) : nextAppointment ? (
          <section className="dashboard-card next-card">
            <div className="card-icon">
              <CalendarCheck />
            </div>

            <div>
              <span className="card-label">Next appointment</span>

              <h2>{formatAppointmentDate(nextAppointment)}</h2>

              <strong>
                {formatAppointmentTime(nextAppointment)}
              </strong>

              <p>
                {getAppointmentDuration(nextAppointment)}

                {getAppointmentDuration(nextAppointment) &&
                  getTherapistName(nextAppointment) &&
                  " with "}

                {getTherapistName(nextAppointment)}
              </p>
            </div>

            <button
              type="button"
              className="secondary-btn"
              onClick={viewNextAppointment}
            >
              View details
            </button>
          </section>
        ) : (
          <section className="dashboard-card next-card">
            <div className="card-icon">
              <CalendarPlus />
            </div>

            <div>
              <span className="card-label">
                Upcoming appointments
              </span>

              <h2>Nothing booked</h2>

              <p>You currently have no upcoming appointments.</p>
            </div>

            {profile?.canSelfBookAppointments && (
              <button
                type="button"
                className="secondary-btn"
                onClick={() => navigate("/bookAppointment")}
              >
                Book an appointment
              </button>
            )}
          </section>
        )}

        <section className="dashboard-card">
          <h3>Appointments</h3>

          <button
            type="button"
            className="quick-link"
            onClick={() => navigate("/appointments")}
          >
            <div>
              <Calendar2Week />
              <span>View appointments</span>
            </div>

            <ChevronRight />
          </button>

          {profile?.canSelfBookAppointments && (
            <button
              type="button"
              className="quick-link"
              onClick={() => navigate("/bookAppointment")}
            >
              <div>
                <CalendarPlus />
                <span>Book an appointment</span>
              </div>

              <ChevronRight />
            </button>
          )}
        </section>
      </div>
    </div>
  );
}